/**
 * Sidebar-header workspace switcher. Combobox-style: a large sidebar menu
 * button shows the current workspace; clicking opens a Popover containing a
 * Command palette for search + select + "Create new workspace".
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 4.5`.
 *
 * Data flow: `authClient.listWorkspaces()` via TanStack Query (cached
 * 30s, refetched on focus). Selecting an item navigates to that
 * workspace's home; `<WorkspaceProvider>` notices the URL change and asks
 * the API to rotate the session.
 *
 * "Create new workspace" opens an inline dialog that POSTs to
 * `/api/workspaces`. With multi-workspace-per-org, the server places the new
 * workspace under the caller's current org (no new org); navigating into it
 * rotates the session.
 */

import {
  authClient,
  useAbility,
  useWorkspace,
  type AuthClientError,
} from "@cometchat-ai/auth/client";
import { createWorkspaceSchema, type CreateWorkspaceDto } from "@cometchat-ai/auth/shared";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  toast,
  useSidebar,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, PanelLeft, Plus } from "./icons";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { formatApiError } from "../lib/format-error";
import { WorkspaceIcon } from "./workspace-icon";

export function WorkspaceSwitcher() {
  const { workspace } = useWorkspace();
  const { t } = useTranslation("workspace");
  const { isMobile, state, toggleSidebar } = useSidebar();
  const ability = useAbility();
  // Mirror the server's `workspace:create` check (assertCanCreateWorkspace):
  // hide the create affordance entirely rather than let a member hit a 403.
  const canCreate = ability.can("create", "Workspace");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: workspaces } = useQuery({
    queryKey: ["workspaces", "list"],
    queryFn: () => authClient.listWorkspaces(),
    staleTime: 30_000,
  });

  // Collapsed rail: the workspace tile doubles as the re-expand control —
  // it swaps to a sidebar glyph on hover and expands the sidebar on click
  // (the inline collapse trigger is hidden in icon mode).
  if (state === "collapsed" && !isMobile) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            onClick={toggleSidebar}
            aria-label={t("switcher.expandLabel")}
            className="group/wsexpand h-9 !px-0 hover:!bg-transparent group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:justify-center"
            data-testid="topbar-sidebar-expand"
          >
            <div className="relative flex size-6 shrink-0 items-center justify-center">
              <WorkspaceIcon
                src={workspace.logoUrl}
                className="size-6 transition-opacity group-hover/wsexpand:opacity-0"
              />
              <PanelLeft className="absolute size-3.5 opacity-0 transition-opacity group-hover/wsexpand:opacity-100" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              role="combobox"
              aria-expanded={open}
              aria-label={t("switcher.label")}
              className="h-9 !px-0 hover:!bg-transparent data-[state=open]:!bg-transparent group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:justify-center"
              data-testid="topbar-workspace-switcher"
            >
              <WorkspaceIcon src={workspace.logoUrl} className="size-6 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium group-data-[collapsible=icon]:hidden">
                {workspace.name}
              </span>
              <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)] group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-56 p-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <Command>
              <CommandInput placeholder={t("switcher.searchPlaceholder")} />
              <CommandList>
                <CommandEmpty>{t("switcher.noResults")}</CommandEmpty>
                {workspaces && workspaces.length > 0 ? (
                  <CommandGroup heading={t("switcher.heading")}>
                    {workspaces.map((ws) => (
                      <CommandItem
                        key={ws.id}
                        value={ws.slug}
                        onSelect={() => {
                          setOpen(false);
                          if (ws.slug !== workspace.slug) {
                            navigate(`/w/${ws.slug}`);
                          }
                        }}
                        data-testid={`topbar-workspace-switcher-item-${ws.slug}`}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            ws.slug === workspace.slug ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="truncate">{ws.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
                {canCreate ? (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => {
                          setOpen(false);
                          setCreateOpen(true);
                        }}
                        data-testid="topbar-workspace-switcher-create"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("switcher.createNew")}
                      </CommandItem>
                    </CommandGroup>
                  </>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {canCreate ? (
          <CreateWorkspaceDialog open={createOpen} onOpenChange={setCreateOpen} />
        ) : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * Inline "create a workspace under my current org" dialog. The server resolves
 * the target org from the caller's session/org-membership and creates a
 * local-only workspace there; navigating into it rotates the session.
 */
function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("workspace");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CreateWorkspaceDto>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: "", logoUrl: "" },
  });

  const create = useMutation({
    mutationFn: (values: CreateWorkspaceDto) => authClient.createWorkspace(values),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      onOpenChange(false);
      form.reset();
      // Navigating into the new workspace rotates the session via
      // <WorkspaceProvider>; the workspace starts on the onboarding surface.
      navigate(`/w/${result.workspace.slug}`);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("createWorkspace.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-workspace-dialog">
        <DialogHeader>
          <DialogTitle>{t("createWorkspace.title")}</DialogTitle>
          <DialogDescription>{t("createWorkspace.subtitle")}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => create.mutate(values))}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("createWorkspace.name.label")}</FormLabel>
                  <FormControl>
                    <Input autoFocus data-testid="create-workspace-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logoUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("createWorkspace.icon.label")}</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      inputMode="url"
                      placeholder={t("createWorkspace.icon.placeholder")}
                      data-testid="create-workspace-icon"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="submit"
                disabled={create.isPending}
                data-testid="create-workspace-submit"
              >
                {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {create.isPending ? t("createWorkspace.submitting") : t("createWorkspace.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
